import type { ReactNode } from 'react';
import { useLoaderData } from 'react-router';
import { EyeIcon, BookmarkIcon, BookOpenIcon } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { shortenCount, formatTimeSpent } from '@rocco/utils/format-number';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

/**
 * Engagement-stats pills (Views · Saves · Time spent) from the project KPI,
 * laid out by the host: About the Project sets them under its description.
 */
export const ProjectStats = ({ className }: { className?: string }) => {
    const { project } = useLoaderData<typeof loader>();
    const kpi = project.PublicKPI;
    if (!kpi) return null;

    const totalViews = (kpi.ItemPageViews ?? 0) + (kpi.ItemImpressions ?? 0);
    const stats: { icon: ReactNode; value: string }[] = [];
    if (totalViews > 100) {
        stats.push({
            icon: <EyeIcon className="size-5 text-white" />,
            value: shortenCount(totalViews),
        });
    }
    if ((kpi.ItemSaves ?? 0) > 100) {
        stats.push({
            icon: <BookmarkIcon className="size-5 text-white" />,
            value: shortenCount(kpi.ItemSaves ?? 0),
        });
    }
    if ((kpi.ItemTimeSpent ?? 0) > 0) {
        stats.push({
            icon: <BookOpenIcon className="size-5 text-white" />,
            value: formatTimeSpent(kpi.ItemTimeSpent ?? 0),
        });
    }
    if (stats.length === 0) return null;

    return (
        <div
            className={cn(
                'flex flex-wrap items-center gap-8 text-white max-md:gap-2.5',
                className,
            )}
        >
            {stats.map((stat, index) => (
                <span
                    key={index}
                    // min-width (not a fixed width) so the pills keep a uniform
                    // baseline but a longer value like "890h 22m" expands on one
                    // line instead of wrapping inside a fixed w-32. On mobile the
                    // three pills share the row, so drop the min-width and tighten
                    // the padding so they fit without overflowing the gutter.
                    className="flex h-10 min-w-32 items-center justify-center gap-2 rounded-[var(--radius-btn-m)] border border-[#767676] py-3 font-sans whitespace-nowrap max-md:min-w-0 max-md:px-3.5 max-md:text-sm md:px-6 md:text-[1.128rem]"
                >
                    {stat.icon}
                    {stat.value}
                </span>
            ))}
        </div>
    );
};
